import { echoStationAuthoredData } from './echoStationAuthoredData.js';
import { productionSetups } from './productionSetups.js';

const actors = { Vale: 'vale', Elias: 'elias', Rhea: 'rhea' };
const voices = { vale: 'af_bella', elias: 'bm_fable', rhea: 'bm_fable' };
const allActors = Object.values(actors);

export function buildExpandedEchoStation(base) {
  if (base.id !== 'echo-station-signal') throw new Error('Expected the existing Echo Station scenario.');
  const result = structuredClone(base);
  const usedAnchors = new Set();
  result.catalogRevision = 2;
  result.duration = 'Full story · 483 cues';
  result.scenes = echoStationAuthoredData.scenes.map(({ firstCue, lastCue, ...scene }) => ({
    ...scene, tone: 'Evidence, privacy, and a shared decision',
  }));
  result.cues = echoStationAuthoredData.cues.map(authored => {
    const characterId = actors[authored.actor];
    const ending = authored.actor === 'GROUP DECISION BEAT';
    const matches = base.cues.filter(cue => ending
      ? cue.type === 'ending' && cue.text === authored.directionText
      : characterId && cue.characterId === characterId && (cue.dialogueText || cue.text) === authored.dialogueText);
    if (matches.length > 1) throw new Error('Ambiguous Echo Station source anchor.');
    const anchor = matches[0];
    if (anchor) usedAnchors.add(anchor.id);
    const common = {
      id: anchor?.id || `echo-c${String(authored.number).padStart(3, '0')}`,
      sceneId: authored.sceneId, label: anchor?.label || `Cue ${authored.number} · ${authored.actor}`,
      directionText: authored.directionText, spokenDirectionText: authored.spokenDirectionText, requireAck: true,
    };
    if (characterId) return {
      ...common, type: 'dialogue', visibility: 'character', characterId,
      text: authored.dialogueText, dialogueText: authored.dialogueText, cueVoice: true,
    };
    if (!ending && authored.actor !== 'GROUP PERFORMANCE BEAT') throw new Error('Unknown Echo Station actor.');
    return {
      ...common, type: ending ? 'ending' : 'action', visibility: 'selected', characterIds: [...allActors],
      text: authored.directionText, directorVoice: true, completionMode: 'director',
    };
  });
  if (usedAnchors.size !== base.cues.length) throw new Error('Echo Station expansion lost a source anchor.');
  result.characterVoices = { version: 1, assignments: Object.fromEntries(allActors.map(id => [
    id, { provider: 'voicebox', nativeVoiceId: voices[id], source: 'manual' },
  ])) };
  delete result.actorBriefs;
  result.script = result.cues.map(cue => ({
    type: cue.type === 'dialogue' ? 'dialogue' : 'direction', sceneId: cue.sceneId,
    characterId: cue.characterId, speaker: result.characters.find(character => character.id === cue.characterId)?.name,
    text: cue.dialogueText || cue.text,
  }));
  return result;
}

// Retained v1 staging stays in productionSetups. Select these notes only for
// the exact reviewed expanded script; old rooms keep their saved three scenes.
export const expandedEchoProductionSetup = {
  ...structuredClone(productionSetups['echo-station-signal']),
  setupVersion: 'v2', introAudio: false,
  scenes: echoStationAuthoredData.scenes.map(() => ({
    setting: 'Echo Station, beside the maintenance receiver and console.',
    props: ['console', 'note', 'clock', 'speaker', 'message'],
    staging: 'Use a pretend receiver and console. Keep paper evidence nearby; reveal it only when your private instructions call for it.',
    intro: 'You are at Echo Station beside the receiver and console. Listen for your private instructions.',
  })),
};
